import React from 'react';
import { AlertTriangle, Check, RefreshCw, Sparkles, X } from 'lucide-react';
import { Button } from '../ui/Button';
import { Skeleton } from '../ui/Skeleton';
import { AiScoreGauge } from './AiScoreGauge';
import { usePlatform } from '../../contexts/PlatformContext';
import { useAiRun } from '../../hooks/useAiRun';
import { weightedScore, type Bands } from '../../utils/scoring';
import type { ApplicationRecord } from '../../types/domain';
const RUN_STAGES = ['Reading submitted documents', 'Matching preset criteria set', 'Scoring against weighted criteria', 'Detecting risk flags and gaps', 'Ranking preset recommendations'];
interface AiInsightRailProps {
  record: ApplicationRecord;
  bands?: Bands;
  criteriaSetLabel: string;
}
export function AiInsightRail({
  record,
  bands,
  criteriaSetLabel
}: AiInsightRailProps) {
  const {
    setRecommendationStatus
  } = usePlatform();
  const run = useAiRun({
    stages: RUN_STAGES,
    stageDuration: 700
  });
  const score = weightedScore(record.criteria);
  const running = run.state === 'running';
  return <aside className="flex flex-col gap-4 rounded-sm border-l-4 border-uae-gold bg-uae-navy p-4 text-white" data-id="element-49" data-mp="dp4a8b5zr7gn">
      <div className="flex items-start justify-between gap-3" data-id="element-50" data-mp="8qausd7596du">
        <div data-id="element-51" data-mp="njw9rkj4w4e2">
          <p className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-[0.12em] text-white/60" data-id="element-52" data-mp="nkfpqvp84ipg">
            <Sparkles className="h-3.5 w-3.5" aria-hidden="true" data-id="element-53" data-mp="ik9vr8vhek47" />
            AI assessment
          </p>
          <p className="mt-1 text-[11px] text-white/55" data-id="element-54" data-mp="3m2urdfda5dgb">{criteriaSetLabel}</p>
        </div>
        <Button variant="ghost" size="xs" onClick={run.start} disabled={running} className="text-white/75 hover:bg-white/10 hover:text-white" data-id="element-55" data-mp="3m103bs6pp580">
          <RefreshCw className={`mr-1 h-3 w-3 ${running ? 'animate-spin' : ''}`} aria-hidden="true" data-id="element-56" data-mp="3vz6426gs85ba" />
          {running ? 'Scoring' : 'Re-run'}
        </Button>
      </div>

      {running ? <div className="space-y-3" aria-live="polite" data-id="element-57" data-mp="3t10mi3aiw2x0">
          <div className="flex items-center gap-4" data-id="element-58" data-mp="3syeg46zdbtgd">
            <Skeleton className="h-[104px] w-[104px] rounded-full bg-white/10" data-id="element-59" data-mp="vwp474pm0g9" />
            <div className="flex-1 space-y-2" data-id="element-60" data-mp="5noewfg1k25c">
              <Skeleton className="h-3 w-24 bg-white/10" data-id="element-61" data-mp="5ny6u8kncjdo" />
              <Skeleton className="h-3 w-32 bg-white/10" data-id="element-62" data-mp="an177fpom1fb" />
            </div>
          </div>
          <div className="rounded-sm bg-white/5 p-3" data-id="element-63" data-mp="3a24sarynzoj6">
            <p className="text-[11px] text-white/70" data-id="element-64" data-mp="3a3fuwogdiq8p">{run.currentStage}</p>
            <div className="mt-2 h-1 w-full overflow-hidden rounded-full bg-white/10" data-id="element-65" data-mp="3e3zr9pt4hpts">
              <div className="h-full bg-white/60 transition-all duration-500" style={{
            width: `${run.progress}%`
          }} data-id="element-66" data-mp="3j19qfx6bdfut" />
            </div>
          </div>
        </div> : <AiScoreGauge score={score} bands={bands} caption={`Scored across ${record.criteria.length} weighted criteria. Every criterion is evidence-linked and can be overridden by the assigned officer.`} data-id="element-67" data-mp="ybkbzl9ivw2n" />}

      <div data-id="element-68" data-mp="2iuef1048akl0">
        <p className="text-[11px] font-semibold uppercase tracking-[0.1em] text-white/55" data-id="element-69" data-mp="2itl6pupmfn0r">
          Criterion breakdown
        </p>
        <ul className="mt-2 space-y-2.5" data-id="element-70" data-mp="2e1tllzp8i9i0">
          {record.criteria.map(criterion => <li key={criterion.id} data-id="element-71" data-mp="293jr7rbc3cxg">
              <div className="flex items-baseline justify-between gap-3" data-id="element-72" data-mp="292gz5g29z4fy">
                <span className="text-[12px] text-white/90" data-id="element-73" data-mp="25416ownfd3v5">{criterion.label}</span>
                <span className="shrink-0 font-mono text-[11px] text-white/70" data-id="element-74" data-mp="202u0h20xkcql">
                  {criterion.score} · w{criterion.weight}
                </span>
              </div>
              <div className="mt-1 h-1.5 w-full overflow-hidden rounded-full bg-white/10" data-id="element-75" data-mp="1q68uadd6cvl6">
                <div className="h-full rounded-full bg-white/55" style={{
              width: `${criterion.score}%`
            }} data-id="element-76" data-mp="3pitdiu7o6mbi" />
              </div>
              <p className="mt-1 text-[11px] leading-relaxed text-white/50" data-id="element-77" data-mp="3skfpjagporv7">
                {criterion.evidence}
              </p>
            </li>)}
        </ul>
      </div>

      {record.flags.length > 0 && <div data-id="element-78" data-mp="3imruejf71ici">
          <p className="text-[11px] font-semibold uppercase tracking-[0.1em] text-white/55" data-id="element-79" data-mp="3ikyox7gbh463">
            Risk flags
          </p>
          <ul className="mt-2 space-y-2" data-id="element-80" data-mp="3nn6405wg10cn">
            {record.flags.map(flag => <li key={flag.id} className="rounded-sm bg-white/5 p-2.5" data-id="element-81" data-mp="39s3meli8hmk0">
                <p className="flex items-start gap-1.5 text-[12px] font-medium text-white" data-id="element-82" data-mp="3elgc6yynfzec">
                  <AlertTriangle className={`mt-0.5 h-3.5 w-3.5 shrink-0 ${flag.severity === 'critical' ? 'text-[#ff9d9b]' : 'text-[#f0c94f]'}`} aria-hidden="true" data-id="element-83" data-mp="2zwa5yr1e98wy" />
                  {flag.label}
                </p>
                <p className="mt-1 pl-5 text-[11px] leading-relaxed text-white/60" data-id="element-84" data-mp="2uv4c2pvzzflu">
                  {flag.detail}
                </p>
              </li>)}
          </ul>
        </div>}

      <div data-id="element-85" data-mp="27alhthjmqgue">
        <p className="text-[11px] font-semibold uppercase tracking-[0.1em] text-white/55" data-id="element-86" data-mp="27av7t54oco03">
          Recommendations
        </p>
        {record.recommendations.length === 0 ? <p className="mt-2 rounded-sm bg-white/5 p-2.5 text-[11px] text-white/60" data-id="element-87" data-mp="2g8xikce7mv0k">
            No preset rule triggered for this record.
          </p> : <ul className="mt-2 space-y-2" data-id="element-88" data-mp="2q8el4ky60ssb">
            {record.recommendations.map(recommendation => <li key={recommendation.id} className="rounded-sm bg-white/5 p-2.5" data-id="element-89" data-mp="33zjdr3348kt5">
                <div className="flex items-start justify-between gap-2" data-id="element-90" data-mp="2zxy7i0l374rx">
                  <p className="text-[12px] font-medium leading-snug text-white" data-id="element-91" data-mp="30026c444pdlc">
                    {recommendation.title}
                  </p>
                  <span className="shrink-0 font-mono text-[10px] text-white/50" data-id="element-92" data-mp="38z5tbj6f2d54">
                    {recommendation.confidence}%
                  </span>
                </div>
                <p className="mt-1 text-[11px] leading-relaxed text-white/60" data-id="element-93" data-mp="2ama6ylkpn4sr">
                  {recommendation.detail}
                </p>
                <p className="mt-1.5 font-mono text-[10px] uppercase tracking-wide text-white/40" data-id="element-94" data-mp="2fkv0k25giq88">
                  rule {recommendation.ruleId}
                </p>
                {recommendation.status === 'pending' ? <div className="mt-2 flex gap-1.5" data-id="element-95" data-mp="2pdk66gebt9vn">
                    <Button size="xs" variant="secondary" className="bg-white/90 text-uae-navy hover:bg-white" onClick={() => setRecommendationStatus(record.id, recommendation.id, 'accepted')} data-id="element-96" data-mp="2u9hnoq1bsma0">
                      <Check className="mr-1 h-3 w-3" aria-hidden="true" data-id="element-97" data-mp="140heunsfbciw" />
                      Accept
                    </Button>
                    <Button size="xs" variant="ghost" className="text-white/70 hover:bg-white/10 hover:text-white" onClick={() => setRecommendationStatus(record.id, recommendation.id, 'overridden')} data-id="element-98" data-mp="11e715hj8bsu5">
                      <X className="mr-1 h-3 w-3" aria-hidden="true" data-id="element-99" data-mp="mmppor187rto" />
                      Override
                    </Button>
                  </div> : <p className="mt-2 text-[11px] text-white/70" data-id="element-100" data-mp="1fdzlmv45xcto">
                    {recommendation.status === 'accepted' ? 'Accepted by officer — recorded on the timeline' : 'Overridden by officer — recorded on the timeline'}
                  </p>}
              </li>)}
          </ul>}
      </div>
    </aside>;
}